import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Volume2,
  VolumeX,
  Loader2,
  AlertCircle,
  User,
  Bot,
  CheckCircle2,
  Lightbulb,
  ChevronRight,
  Sparkles,
  MessageSquareQuote
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { useVideoRecorder } from './useVideoRecorder';

// Translations for the UI
const translations = {
  en: {
    live: 'LIVE',
    speaking: 'Speaking...',
    youAreSpeaking: 'You are speaking...',
    connected: 'Connected',
    connecting: 'Connecting...',
    error: 'Error',
    disconnected: 'Disconnected',
    startInterview: 'Start Interview',
    endInterview: 'End Interview',
    conversation: 'Conversation',
    clickToStart: 'Click "Start Interview" to begin the conversation',
    answerHints: 'Answer Hints',
    listenAndPrepare: 'Listen & Prepare',
    questionOf: 'Question {current} of {total}',
    trySaying: 'Try saying...',
    tips: 'Tips:',
    allQuestions: 'All Questions',
    aboutThisInterview: 'About This Interview',
    aiVoiceInterview: 'AI Voice Interview',
    aiVoiceDescription: "You'll have a natural conversation with {name}, an AI journalist. Speak naturally and answer questions as they come.",
    howItWorks: 'How it works',
    howItWorksList: [
      'Click "Start Interview" to begin',
      'Speak naturally when asked questions',
      'The AI will listen and respond in real-time',
      'Click "End Interview" when finished'
    ],
    tipsTitle: 'Tips',
    tipsList: [
      'Use a quiet environment',
      'Speak clearly and at a normal pace',
      'Take your time with answers',
      'Use the mute button if you need a break'
    ],
    sessionInfo: 'Session Info',
    sessionId: 'Session ID',
    questions: 'Questions',
    transcripts: 'Transcripts',
    exchanges: 'exchanges',
    hintsWillAppear: 'Hints will appear here once the interview starts',
    connectionError: 'Connection error. Please check your network.',
    cameraError: 'Failed to access camera. Please check your camera permissions.',
    interviewSavedVideoFailed: 'Interview saved, but video upload failed. Please contact support.',
    interviewSavedRecordingFailed: 'Interview saved, but video recording failed.',
    failedToSaveInterview: 'Failed to save interview. Please try again.',
    // Dynamic hint generation strings
    hintShareExperience: 'Share your personal experience or perspective',
    hintMentionBenefits: "Mention specific benefits you've observed",
    hintWalkThrough: 'Walk through the steps or process',
    hintShareTips: 'Share practical tips from your experience',
    hintGiveRecommendation: 'Give a clear recommendation',
    hintExplainStandout: 'Explain what makes it stand out',
    hintHighlightDifferences: 'Highlight 2-3 key differences',
    hintMentionPreference: 'Mention which you prefer and why',
    hintSharePitfalls: "Share common pitfalls you've seen",
    hintAdviceAvoid: 'Give advice on how to avoid them',
    hintSharePerspective: 'Share your expert perspective',
    hintUseExamples: 'Use concrete examples when possible',
    hintBeSpecific: 'Be specific and authentic',
    // Example answer templates
    exampleMainReason: '"The main reason I recommend {subject} is..."',
    exampleBiggestAdvantage: '"In my experience, the biggest advantage is..."',
    exampleSetsApart: '"What really sets it apart is..."',
    exampleFirstThing: '"The first thing I always do is..."',
    exampleKeySteps: '"The key steps are: first... then... and finally..."',
    exampleTimeSavingTip: '"A tip that saves a lot of time is..."',
    exampleTopPick: '"My top pick would be... because..."',
    exampleRecommend: "\"For most people, I'd recommend...\"",
    examplePersonallyUse: '"What I personally use is..."',
    exampleMainDifference: '"The main difference is..."',
    exampleIfLookingFor: "\"If you're looking for X, go with... but for Y, choose...\"",
    exampleWhereDiffer: '"Where they really differ is..."',
    exampleMistake: '"The #1 mistake I see is..."',
    exampleOverlooked: '"What people often overlook is..."',
    exampleAvoidProblems: '"To avoid problems, always make sure to..."',
    exampleWhatMatters: '"What matters most is..."',
    exampleCrucialThing: '"The crucial thing to look for is..."',
    exampleDontOverlook: "\"Don't overlook...\"",
    exampleRemember: '"I remember when..."',
    exampleGreatExample: '"A great example is..."',
    exampleRecentCustomer: '"Recently, I had a customer who..."',
    exampleFromWhatSeen: "\"From what I've seen...\"",
    exampleHonestOpinion: '"My honest opinion is..."',
    exampleBasedOnExperience: '"Based on my experience..."',
    exampleWhatFound: "\"What I've found is...\"",
    exampleKeyThing: '"The key thing to understand is..."',
    exampleInMyExperience: '"In my experience..."'
  },
  es: {
    live: 'EN VIVO',
    speaking: 'Hablando...',
    youAreSpeaking: 'Estás hablando...',
    connected: 'Conectado',
    connecting: 'Conectando...',
    error: 'Error',
    disconnected: 'Desconectado',
    startInterview: 'Iniciar Entrevista',
    endInterview: 'Finalizar Entrevista',
    conversation: 'Conversación',
    clickToStart: 'Haga clic en "Iniciar Entrevista" para comenzar la conversación',
    answerHints: 'Sugerencias de Respuesta',
    listenAndPrepare: 'Escuche y Prepárese',
    questionOf: 'Pregunta {current} de {total}',
    trySaying: 'Intente decir...',
    tips: 'Consejos:',
    allQuestions: 'Todas las Preguntas',
    aboutThisInterview: 'Sobre Esta Entrevista',
    aiVoiceInterview: 'Entrevista por Voz con IA',
    aiVoiceDescription: 'Tendrá una conversación natural con {name}, un periodista de IA. Hable naturalmente y responda las preguntas a medida que surjan.',
    howItWorks: 'Cómo funciona',
    howItWorksList: [
      'Haga clic en "Iniciar Entrevista" para comenzar',
      'Hable naturalmente cuando le hagan preguntas',
      'La IA escuchará y responderá en tiempo real',
      'Haga clic en "Finalizar Entrevista" cuando termine'
    ],
    tipsTitle: 'Consejos',
    tipsList: [
      'Use un ambiente tranquilo',
      'Hable claramente y a un ritmo normal',
      'Tómese su tiempo con las respuestas',
      'Use el botón de silencio si necesita un descanso'
    ],
    sessionInfo: 'Info de Sesión',
    sessionId: 'ID de Sesión',
    questions: 'Preguntas',
    transcripts: 'Transcripciones',
    exchanges: 'intercambios',
    hintsWillAppear: 'Las sugerencias aparecerán aquí una vez que comience la entrevista',
    connectionError: 'Error de conexión. Por favor, compruebe su red.',
    cameraError: 'No se pudo acceder a la cámara. Por favor, compruebe los permisos de su cámara.',
    interviewSavedVideoFailed: 'Entrevista guardada, pero la carga del video falló. Por favor, contacte con soporte.',
    interviewSavedRecordingFailed: 'Entrevista guardada, pero la grabación del video falló.',
    failedToSaveInterview: 'No se pudo guardar la entrevista. Por favor, inténtelo de nuevo.',
    // Dynamic hint generation strings
    hintShareExperience: 'Comparta su experiencia o perspectiva personal',
    hintMentionBenefits: 'Mencione los beneficios específicos que ha observado',
    hintWalkThrough: 'Explique los pasos o el proceso',
    hintShareTips: 'Comparta consejos prácticos de su experiencia',
    hintGiveRecommendation: 'Dé una recomendación clara',
    hintExplainStandout: 'Explique qué lo hace destacar',
    hintHighlightDifferences: 'Destaque 2-3 diferencias clave',
    hintMentionPreference: 'Mencione cuál prefiere y por qué',
    hintSharePitfalls: 'Comparta errores comunes que ha visto',
    hintAdviceAvoid: 'Dé consejos sobre cómo evitarlos',
    hintSharePerspective: 'Comparta su perspectiva experta',
    hintUseExamples: 'Use ejemplos concretos cuando sea posible',
    hintBeSpecific: 'Sea específico y auténtico',
    // Example answer templates
    exampleMainReason: '"La razón principal por la que recomiendo {subject} es..."',
    exampleBiggestAdvantage: '"En mi experiencia, la mayor ventaja es..."',
    exampleSetsApart: '"Lo que realmente lo distingue es..."',
    exampleFirstThing: '"Lo primero que siempre hago es..."',
    exampleKeySteps: '"Los pasos clave son: primero... luego... y finalmente..."',
    exampleTimeSavingTip: '"Un consejo que ahorra mucho tiempo es..."',
    exampleTopPick: '"Mi primera elección sería... porque..."',
    exampleRecommend: '"Para la mayoría de personas, recomendaría..."',
    examplePersonallyUse: '"Lo que yo personalmente uso es..."',
    exampleMainDifference: '"La diferencia principal es..."',
    exampleIfLookingFor: '"Si busca X, elija... pero para Y, escoja..."',
    exampleWhereDiffer: '"Donde realmente difieren es..."',
    exampleMistake: '"El error #1 que veo es..."',
    exampleOverlooked: '"Lo que la gente a menudo pasa por alto es..."',
    exampleAvoidProblems: '"Para evitar problemas, siempre asegúrese de..."',
    exampleWhatMatters: '"Lo que más importa es..."',
    exampleCrucialThing: '"Lo crucial a buscar es..."',
    exampleDontOverlook: '"No pase por alto..."',
    exampleRemember: '"Recuerdo cuando..."',
    exampleGreatExample: '"Un gran ejemplo es..."',
    exampleRecentCustomer: '"Recientemente, tuve un cliente que..."',
    exampleFromWhatSeen: '"Por lo que he visto..."',
    exampleHonestOpinion: '"Mi opinión honesta es..."',
    exampleBasedOnExperience: '"Basándome en mi experiencia..."',
    exampleWhatFound: '"Lo que he encontrado es..."',
    exampleKeyThing: '"Lo clave a entender es..."',
    exampleInMyExperience: '"En mi experiencia..."'
  },
  ca: {
    live: 'EN DIRECTE',
    speaking: 'Parlant...',
    youAreSpeaking: 'Esteu parlant...',
    connected: 'Connectat',
    connecting: 'Connectant...',
    error: 'Error',
    disconnected: 'Desconnectat',
    startInterview: 'Iniciar Entrevista',
    endInterview: 'Finalitzar Entrevista',
    conversation: 'Conversa',
    clickToStart: 'Feu clic a "Iniciar Entrevista" per començar la conversa',
    answerHints: 'Suggeriments de Resposta',
    listenAndPrepare: 'Escolteu i Prepareu-vos',
    questionOf: 'Pregunta {current} de {total}',
    trySaying: 'Proveu de dir...',
    tips: 'Consells:',
    allQuestions: 'Totes les Preguntes',
    aboutThisInterview: 'Sobre Aquesta Entrevista',
    aiVoiceInterview: 'Entrevista per Veu amb IA',
    aiVoiceDescription: 'Tindreu una conversa natural amb {name}, un periodista d\'IA. Parleu naturalment i responeu les preguntes a mesura que sorgeixin.',
    howItWorks: 'Com funciona',
    howItWorksList: [
      'Feu clic a "Iniciar Entrevista" per començar',
      'Parleu naturalment quan us facin preguntes',
      'La IA escoltarà i respondrà en temps real',
      'Feu clic a "Finalitzar Entrevista" quan acabeu'
    ],
    tipsTitle: 'Consells',
    tipsList: [
      'Utilitzeu un ambient tranquil',
      'Parleu clarament i a un ritme normal',
      'Preneu-vos el vostre temps amb les respostes',
      'Utilitzeu el botó de silenci si necessiteu un descans'
    ],
    sessionInfo: 'Info de Sessió',
    sessionId: 'ID de Sessió',
    questions: 'Preguntes',
    transcripts: 'Transcripcions',
    exchanges: 'intercanvis',
    hintsWillAppear: 'Els suggeriments apareixeran aquí un cop comenci l\'entrevista',
    connectionError: 'Error de connexió. Si us plau, comproveu la vostra xarxa.',
    cameraError: 'No s\'ha pogut accedir a la càmera. Si us plau, comproveu els permisos de la càmera.',
    interviewSavedVideoFailed: 'Entrevista guardada, però la càrrega del vídeo ha fallat. Si us plau, contacteu amb suport.',
    interviewSavedRecordingFailed: 'Entrevista guardada, però la gravació del vídeo ha fallat.',
    failedToSaveInterview: 'No s\'ha pogut guardar l\'entrevista. Si us plau, torneu-ho a provar.',
    // Dynamic hint generation strings
    hintShareExperience: 'Compartiu la vostra experiència o perspectiva personal',
    hintMentionBenefits: 'Mencioneu els beneficis específics que heu observat',
    hintWalkThrough: 'Expliqueu els passos o el procés',
    hintShareTips: 'Compartiu consells pràctics de la vostra experiència',
    hintGiveRecommendation: 'Doneu una recomanació clara',
    hintExplainStandout: 'Expliqueu què el fa destacar',
    hintHighlightDifferences: 'Destaqueu 2-3 diferències clau',
    hintMentionPreference: 'Mencioneu quin preferiu i per què',
    hintSharePitfalls: 'Compartiu errors comuns que heu vist',
    hintAdviceAvoid: 'Doneu consells sobre com evitar-los',
    hintSharePerspective: 'Compartiu la vostra perspectiva experta',
    hintUseExamples: 'Utilitzeu exemples concrets quan sigui possible',
    hintBeSpecific: 'Sigueu específic i autèntic',
    // Example answer templates
    exampleMainReason: '"La raó principal per la qual recomano {subject} és..."',
    exampleBiggestAdvantage: '"En la meva experiència, el major avantatge és..."',
    exampleSetsApart: '"El que realment el distingeix és..."',
    exampleFirstThing: '"El primer que sempre faig és..."',
    exampleKeySteps: '"Els passos clau són: primer... després... i finalment..."',
    exampleTimeSavingTip: '"Un consell que estalvia molt de temps és..."',
    exampleTopPick: '"La meva primera elecció seria... perquè..."',
    exampleRecommend: '"Per a la majoria de persones, recomanaria..."',
    examplePersonallyUse: '"El que jo personalment utilitzo és..."',
    exampleMainDifference: '"La diferència principal és..."',
    exampleIfLookingFor: '"Si busqueu X, trieu... però per a Y, escolliu..."',
    exampleWhereDiffer: '"On realment difereixen és..."',
    exampleMistake: '"L\'error #1 que veig és..."',
    exampleOverlooked: '"El que la gent sovint passa per alt és..."',
    exampleAvoidProblems: '"Per evitar problemes, sempre assegureu-vos de..."',
    exampleWhatMatters: '"El que més importa és..."',
    exampleCrucialThing: '"El crucial a buscar és..."',
    exampleDontOverlook: '"No passeu per alt..."',
    exampleRemember: '"Recordo quan..."',
    exampleGreatExample: '"Un gran exemple és..."',
    exampleRecentCustomer: '"Recentment, vaig tenir un client que..."',
    exampleFromWhatSeen: '"Pel que he vist..."',
    exampleHonestOpinion: '"La meva opinió honesta és..."',
    exampleBasedOnExperience: '"Basant-me en la meva experiència..."',
    exampleWhatFound: '"El que he trobat és..."',
    exampleKeyThing: '"El clau a entendre és..."',
    exampleInMyExperience: '"En la meva experiència..."'
  }
};

type Language = keyof typeof translations;

interface QuestionHint {
  id: string;
  text: string;
}

interface InterviewQuestion {
  id: string;
  text: string;
  type: string;
  hints?: QuestionHint[];
  talkingPoints?: string[];
}

interface RealtimeVoiceInterfaceProps {
  interview: {
    id: string;
    expertName: string;
    language?: string;
    journalist: {
      name: string;
      photoUrl?: string;
    };
    questions: InterviewQuestion[];
  };
  token: string;
  onComplete: () => void;
  selectedAudioDevice?: string;
}

interface TranscriptItem {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export function RealtimeVoiceInterface({ interview, token, onComplete, selectedAudioDevice }: RealtimeVoiceInterfaceProps) {
  // Get translations for the interview language
  const lang = (interview.language || 'en') as Language;
  const t = translations[lang] || translations.en;

  // WebSocket and connection state
  const wsRef = useRef<WebSocket | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Audio state
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioQueueRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);

  // Video state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  // UI state
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcripts, setTranscripts] = useState<TranscriptItem[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState<string>('');
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);

  // Question tracking for hints
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [showHints, setShowHints] = useState(false);
  const questionsAskedRef = useRef<Set<number>>(new Set());

  // Video recording
  const { startRecording, stopRecording, uploadVideo, getAudioDestination, isRecording, recordingError } = useVideoRecorder();

  // References
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll transcripts
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcripts, currentTranscript]);

  // Initialize camera preview on mount
  useEffect(() => {
    initializeCameraPreview();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
      // Also stop camera preview if it's running
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, []);

  /**
   * Initialize camera preview (before interview starts)
   */
  const initializeCameraPreview = async () => {
    try {
      console.log('[RealtimeVoiceInterface] 📷 Initializing camera preview...');

      // Request camera access only (no audio yet)
      const cameraStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      cameraStreamRef.current = cameraStream;

      // Set up video preview
      if (videoRef.current) {
        videoRef.current.srcObject = cameraStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch(err => {
            console.error('[RealtimeVoice] Error playing video preview:', err);
          });
        };
      }

      setCameraReady(true);
      console.log('[RealtimeVoiceInterface] ✅ Camera preview ready');
    } catch (err) {
      console.error('[RealtimeVoiceInterface] Failed to initialize camera preview:', err);
      setError(t.cameraError);
    }
  };

  /**
   * Connect to WebSocket server
   */
  const connect = async () => {
    try {
      setConnectionState('connecting');
      setError(null);

      // Initialize AudioContext
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }

      // Request microphone access (camera is already active from preview)
      console.log('[RealtimeVoiceInterface] 🎤 About to call getUserMedia for microphone with selectedAudioDevice:', selectedAudioDevice);
      const audioStream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: selectedAudioDevice
          ? {
              deviceId: { exact: selectedAudioDevice }, // Use 'exact' to force the selected device
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 24000,
              channelCount: 1
            }
          : {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 24000,
              channelCount: 1
            }
      });
      console.log('[RealtimeVoiceInterface] ✅ getUserMedia for microphone succeeded');

      // Verify which audio device is actually being used
      const audioTrack = audioStream.getAudioTracks()[0];
      if (audioTrack) {
        console.log('[RealtimeVoiceInterface] 🔍 ACTUAL audio device being used:', audioTrack.label);
        console.log('[RealtimeVoiceInterface] 🔍 ACTUAL audio device settings:', audioTrack.getSettings());
      }

      // Combine camera stream (already active) with new audio stream
      let combinedStream: MediaStream;
      if (cameraStreamRef.current) {
        console.log('[RealtimeVoiceInterface] 📹 Using existing camera stream');
        const videoTrack = cameraStreamRef.current.getVideoTracks()[0];
        combinedStream = new MediaStream([videoTrack, audioTrack]);
      } else {
        console.log('[RealtimeVoiceInterface] ⚠️ Camera not ready, requesting camera + audio together');
        // Fallback: request both camera and audio together
        combinedStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: selectedAudioDevice
            ? {
                deviceId: { exact: selectedAudioDevice },
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 24000,
                channelCount: 1
              }
            : {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 24000,
                channelCount: 1
              }
        });
      }

      mediaStreamRef.current = combinedStream;

      // Update video preview with combined stream (if fallback was used)
      if (!cameraStreamRef.current && videoRef.current) {
        videoRef.current.srcObject = combinedStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play().catch(err => {
            console.error('[RealtimeVoice] Error playing video:', err);
          });
        };
      }

      // Start dual recording (video + AI audio)
      console.log('[RealtimeVoice] Starting dual recording...');
      if (audioContextRef.current) {
        startRecording(combinedStream, audioContextRef.current);
      } else {
        console.error('[RealtimeVoice] AudioContext not initialized for recording');
      }

      // Create WebSocket connection
      const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/realtime-voice?token=${encodeURIComponent(token)}&interviewId=${interview.id}`;
      console.log('[RealtimeVoice] Creating WebSocket connection to:', wsUrl);

      const ws = new WebSocket(wsUrl);
      console.log('[RealtimeVoice] WebSocket created, readyState:', ws.readyState);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[RealtimeVoice] WebSocket connected');
        setConnectionState('connected');
      };

      ws.onmessage = (event) => {
        handleServerMessage(JSON.parse(event.data));
      };

      ws.onerror = (error) => {
        console.error('[RealtimeVoice] WebSocket error:', error);
        setError(t.connectionError);
        setConnectionState('error');
      };

      ws.onclose = (event) => {
        console.log('[RealtimeVoice] WebSocket closed, code:', event.code, 'reason:', event.reason);
        setConnectionState('disconnected');

        // Attempt to reconnect after 3 seconds if not manually disconnected
        if (connectionState === 'connected') {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[RealtimeVoice] Attempting to reconnect...');
            connect();
          }, 3000);
        }
      };

      console.log('[RealtimeVoice] WebSocket handlers set up, setting up audio streaming...');

      // Setup audio streaming after connection
      await setupAudioStreaming(combinedStream);
      console.log('[RealtimeVoice] Audio streaming setup complete');

    } catch (err) {
      console.error('[RealtimeVoice] Connection error:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setConnectionState('error');
    }
  };

  /**
   * Setup audio streaming to WebSocket
   */
  const setupAudioStreaming = async (stream: MediaStream) => {
    if (!audioContextRef.current) return;

    const audioContext = audioContextRef.current;
    const source = audioContext.createMediaStreamSource(stream);

    // Create script processor for capturing audio
    const bufferSize = 4096;
    const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);

    processor.onaudioprocess = (e) => {
      if (isMuted || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return;
      }

      const inputData = e.inputBuffer.getChannelData(0);

      // Convert Float32 to PCM16
      const pcm16 = floatToPCM16(inputData);

      // Convert to base64
      const base64Audio = arrayBufferToBase64(pcm16.buffer);

      // Send to server
      wsRef.current.send(JSON.stringify({
        type: 'audio',
        audio: base64Audio
      }));
    };

    source.connect(processor);
    processor.connect(audioContext.destination);
  };

  /**
   * Handle messages from server
   */
  const handleServerMessage = (message: any) => {
    switch (message.type) {
      case 'session.created':
        console.log('[RealtimeVoice] Session created:', message.sessionId);
        setSessionId(message.sessionId);
        break;

      case 'speech.started':
        console.log('[RealtimeVoice] User started speaking');
        setIsSpeaking(true);
        break;

      case 'speech.stopped':
        console.log('[RealtimeVoice] User stopped speaking');
        setIsSpeaking(false);
        break;

      case 'transcript.user':
        console.log('[RealtimeVoice] User transcript:', message.text);
        addTranscript('user', message.text);
        break;

      case 'response.started':
        console.log('[RealtimeVoice] Assistant response started');
        setIsAssistantSpeaking(true);
        setCurrentTranscript('');
        // Show hints when journalist starts speaking
        setShowHints(true);
        break;

      case 'audio.delta':
        // Queue audio for playback
        audioQueueRef.current.push(message.delta);
        playAudioQueue();
        break;

      case 'transcript.delta':
        // Update current transcript
        setCurrentTranscript(prev => prev + message.delta);
        break;

      case 'transcript.assistant':
        console.log('[RealtimeVoice] Assistant transcript:', message.text);
        addTranscript('assistant', message.text);
        setCurrentTranscript('');

        // Try to detect which question was just asked
        const detectedIndex = detectQuestionFromTranscript(message.text);
        if (detectedIndex !== -1 && !questionsAskedRef.current.has(detectedIndex)) {
          console.log('[RealtimeVoice] Detected question:', detectedIndex + 1);
          questionsAskedRef.current.add(detectedIndex);
          setCurrentQuestionIndex(detectedIndex);
        } else if (detectedIndex === -1) {
          // If no specific question detected, advance to next unasked question
          const nextUnaskedIndex = interview.questions.findIndex(
            (_, idx) => !questionsAskedRef.current.has(idx) && idx > currentQuestionIndex
          );
          if (nextUnaskedIndex !== -1) {
            setCurrentQuestionIndex(nextUnaskedIndex);
          }
        }
        break;

      case 'response.done':
        console.log('[RealtimeVoice] Response complete');
        setIsAssistantSpeaking(false);
        break;

      case 'error':
        console.error('[RealtimeVoice] Server error:', message.message);
        setError(message.message);
        break;

      case 'pong':
        // Keepalive response
        break;

      default:
        console.log('[RealtimeVoice] Unknown message type:', message.type);
    }
  };

  /**
   * Play queued audio
   */
  const playAudioQueue = async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) {
      return;
    }

    isPlayingRef.current = true;

    while (audioQueueRef.current.length > 0) {
      const base64Audio = audioQueueRef.current.shift();
      if (!base64Audio) continue;

      try {
        await playAudioChunk(base64Audio);
      } catch (err) {
        console.error('[RealtimeVoice] Error playing audio:', err);
      }
    }

    isPlayingRef.current = false;
  };

  /**
   * Play a single audio chunk
   */
  const playAudioChunk = async (base64Audio: string): Promise<void> => {
    if (!audioContextRef.current) return;

    try {
      // Decode base64 to ArrayBuffer
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert PCM16 to Float32
      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0;
      }

      // Create audio buffer
      const audioBuffer = audioContextRef.current.createBuffer(
        1,
        float32.length,
        audioContextRef.current.sampleRate
      );
      audioBuffer.getChannelData(0).set(float32);

      // Play audio
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;

      // Connect to speakers for playback
      source.connect(audioContextRef.current.destination);

      // ALSO connect to recorder destination for AI audio recording
      const audioDestination = getAudioDestination();
      if (audioDestination) {
        source.connect(audioDestination);
      }

      return new Promise((resolve) => {
        source.onended = () => resolve();
        source.start();
      });
    } catch (err) {
      console.error('[RealtimeVoice] Error decoding audio:', err);
    }
  };

  /**
   * Add transcript item
   */
  const addTranscript = (role: 'user' | 'assistant', text: string) => {
    setTranscripts(prev => [...prev, {
      id: Date.now().toString(),
      role,
      text,
      timestamp: new Date()
    }]);
  };

  /**
   * Detect which question is being asked based on transcript text
   * Returns the index of the matching question, or -1 if no match
   */
  const detectQuestionFromTranscript = (text: string): number => {
    const normalizedText = text.toLowerCase().trim();

    // Check each question for similarity
    for (let i = 0; i < interview.questions.length; i++) {
      const question = interview.questions[i];
      const questionText = question.text.toLowerCase();

      // Extract key words from the question (words > 4 chars)
      const keyWords = questionText
        .split(/\s+/)
        .filter(word => word.length > 4)
        .slice(0, 5); // Use first 5 key words

      // Count how many key words appear in the transcript
      const matchCount = keyWords.filter(word =>
        normalizedText.includes(word.replace(/[?.,!]/g, ''))
      ).length;

      // If more than half the key words match, consider it a match
      if (keyWords.length > 0 && matchCount >= Math.ceil(keyWords.length / 2)) {
        return i;
      }
    }

    return -1;
  };

  /**
   * Generate default hints for a question if none provided
   */
  const getHintsForQuestion = (question: InterviewQuestion): string[] => {
    // If question has hints or talking points, use those
    if (question.hints && question.hints.length > 0) {
      return question.hints.map(h => h.text);
    }
    if (question.talkingPoints && question.talkingPoints.length > 0) {
      return question.talkingPoints;
    }

    // Generate contextual hints based on question type/content
    const text = question.text.toLowerCase();
    const hints: string[] = [];

    // Add question-type specific hints (detect keywords in any language)
    if (text.includes('why') || text.includes('reason') || text.includes('por qué') || text.includes('razón') || text.includes('per què') || text.includes('raó')) {
      hints.push(t.hintShareExperience);
      hints.push(t.hintMentionBenefits);
    } else if (text.includes('how') || text.includes('process') || text.includes('cómo') || text.includes('proceso') || text.includes('com') || text.includes('procés')) {
      hints.push(t.hintWalkThrough);
      hints.push(t.hintShareTips);
    } else if (text.includes('best') || text.includes('recommend') || text.includes('mejor') || text.includes('recomienda') || text.includes('millor') || text.includes('recomana')) {
      hints.push(t.hintGiveRecommendation);
      hints.push(t.hintExplainStandout);
    } else if (text.includes('difference') || text.includes('compare') || text.includes('diferencia') || text.includes('compara') || text.includes('diferència')) {
      hints.push(t.hintHighlightDifferences);
      hints.push(t.hintMentionPreference);
    } else if (text.includes('mistake') || text.includes('avoid') || text.includes('error') || text.includes('evitar')) {
      hints.push(t.hintSharePitfalls);
      hints.push(t.hintAdviceAvoid);
    } else {
      hints.push(t.hintSharePerspective);
      hints.push(t.hintUseExamples);
    }

    // Add universal hints
    hints.push(t.hintBeSpecific);

    return hints;
  };

  /**
   * Generate example answer starters based on the question
   */
  const getExampleAnswers = (question: InterviewQuestion): string[] => {
    const text = question.text.toLowerCase();
    const examples: string[] = [];

    // Extract key subject from question (simplified extraction - works for multiple languages)
    const subjectMatch = text.match(/(?:about|for|with|of|the|sobre|para|con|de|el|la|amb|per)\s+([a-záéíóúüñàèòç\s]+?)(?:\?|$|,|\.)/i);
    const subject = subjectMatch ? subjectMatch[1].trim() : 'esto';

    // Generate contextual example answers based on question type (detect keywords in any language)
    if (text.includes('why') || text.includes('reason') || text.includes('por qué') || text.includes('razón') || text.includes('per què') || text.includes('raó')) {
      examples.push(t.exampleMainReason.replace('{subject}', subject));
      examples.push(t.exampleBiggestAdvantage);
      examples.push(t.exampleSetsApart);
    } else if (text.includes('how do') || text.includes('how to') || text.includes('how can') || text.includes('cómo') || text.includes('com')) {
      examples.push(t.exampleFirstThing);
      examples.push(t.exampleKeySteps);
      examples.push(t.exampleTimeSavingTip);
    } else if (text.includes('best') || text.includes('favorite') || text.includes('recommend') || text.includes('mejor') || text.includes('favorito') || text.includes('recomienda') || text.includes('millor') || text.includes('preferit') || text.includes('recomana')) {
      examples.push(t.exampleTopPick);
      examples.push(t.exampleRecommend);
      examples.push(t.examplePersonallyUse);
    } else if (text.includes('difference') || text.includes('compare') || text.includes('versus') || text.includes(' vs ') || text.includes('diferencia') || text.includes('compara') || text.includes('diferència')) {
      examples.push(t.exampleMainDifference);
      examples.push(t.exampleIfLookingFor);
      examples.push(t.exampleWhereDiffer);
    } else if (text.includes('mistake') || text.includes('avoid') || text.includes('wrong') || text.includes('error') || text.includes('evitar') || text.includes('mal')) {
      examples.push(t.exampleMistake);
      examples.push(t.exampleOverlooked);
      examples.push(t.exampleAvoidProblems);
    } else if (text.includes('important') || text.includes('matter') || text.includes('consider') || text.includes('importante') || text.includes('importa') || text.includes('considera')) {
      examples.push(t.exampleWhatMatters);
      examples.push(t.exampleCrucialThing);
      examples.push(t.exampleDontOverlook);
    } else if (text.includes('experience') || text.includes('story') || text.includes('example') || text.includes('experiencia') || text.includes('historia') || text.includes('ejemplo') || text.includes('experiència') || text.includes('història') || text.includes('exemple')) {
      examples.push(t.exampleRemember);
      examples.push(t.exampleGreatExample);
      examples.push(t.exampleRecentCustomer);
    } else if ((text.includes('what') || text.includes('qué') || text.includes('què')) && (text.includes('think') || text.includes('opinion') || text.includes('piensa') || text.includes('opinión') || text.includes('pensa') || text.includes('opinió'))) {
      examples.push(t.exampleFromWhatSeen);
      examples.push(t.exampleHonestOpinion);
      examples.push(t.exampleBasedOnExperience);
    } else {
      // Generic starters
      examples.push(t.exampleWhatFound);
      examples.push(t.exampleKeyThing);
      examples.push(t.exampleInMyExperience);
    }

    return examples.slice(0, 3); // Return max 3 examples
  };

  /**
   * Disconnect from server
   */
  const disconnect = () => {
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Note: We keep the camera preview running even after disconnect
    // It will be cleaned up on component unmount

    setConnectionState('disconnected');
    setSessionId(null);
  };

  /**
   * Toggle mute
   */
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  /**
   * End interview
   */
  const endInterview = async () => {
    try {
      console.log('[RealtimeVoiceInterface] Ending interview and saving video...');

      // Stop both recordings (video + AI audio)
      console.log('[RealtimeVoiceInterface] Stopping dual recording...');
      const { videoBlob, aiAudioBlob } = await stopRecording();

      // Save transcript
      console.log('[RealtimeVoiceInterface] Saving transcript...');
      const response = await fetch(`${API_URL}/expert/interview/submit-realtime-transcript?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: transcripts })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[RealtimeVoiceInterface] Failed to save transcript:', error);
        throw new Error(error.message || 'Failed to save transcript');
      }

      console.log('[RealtimeVoiceInterface] Transcript saved successfully');

      // Upload video (with or without separate AI audio)
      if (videoBlob) {
        const logInfo = aiAudioBlob
          ? { video: videoBlob.size, aiAudio: aiAudioBlob.size, mixing: 'server-side' }
          : { video: videoBlob.size, mixing: 'client-side (audio already in video)' };

        console.log('[RealtimeVoiceInterface] Uploading video...', logInfo);

        try {
          await uploadVideo(videoBlob, aiAudioBlob, token, interview.id);
          console.log('[RealtimeVoiceInterface] Video uploaded successfully');
        } catch (uploadError) {
          console.error('[RealtimeVoiceInterface] Upload failed:', uploadError);
          // Don't fail the entire interview if upload fails
          setError(t.interviewSavedVideoFailed);
        }
      } else {
        console.error('[RealtimeVoiceInterface] No video blob to upload!');
        setError(t.interviewSavedRecordingFailed);
      }

    } catch (error) {
      console.error('[RealtimeVoiceInterface] Error ending interview:', error);
      setError(t.failedToSaveInterview);
    } finally {
      // Always disconnect and complete
      disconnect();
      onComplete();
    }
  };

  /**
   * Convert Float32Array to PCM16 Int16Array
   */
  const floatToPCM16 = (float32Array: Float32Array): Int16Array => {
    const pcm16 = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return pcm16;
  };

  /**
   * Convert ArrayBuffer to base64
   */
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  /**
   * Format timestamp
   */
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const journalistInitials = interview.journalist.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase();

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Panel - Conversation */}
        <div className="lg:col-span-2 space-y-4">
          {/* Video Preview */}
          <Card>
            <CardContent className="p-0">
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                {connectionState === 'connected' && (
                  <div className="absolute top-4 right-4 flex items-center gap-2 bg-green-600 px-3 py-1 rounded-full">
                    <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    <span className="text-white text-sm font-medium">{t.live}</span>
                  </div>
                )}
                {isSpeaking && (
                  <div className="absolute bottom-4 left-4 bg-primary/90 px-3 py-1 rounded-full">
                    <span className="text-white text-sm font-medium">{t.speaking}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Connection Status */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12 border-2 border-primary">
                    {interview.journalist.photoUrl && (
                      <AvatarImage src={interview.journalist.photoUrl} />
                    )}
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {journalistInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{interview.journalist.name}</p>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        connectionState === 'connected' ? 'bg-green-500 animate-pulse' :
                        connectionState === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                        connectionState === 'error' ? 'bg-red-500' :
                        'bg-gray-400'
                      }`} />
                      <span className="text-sm text-muted-foreground">
                        {connectionState === 'connected' ? t.connected :
                         connectionState === 'connecting' ? t.connecting :
                         connectionState === 'error' ? t.error :
                         t.disconnected}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {connectionState === 'disconnected' ? (
                    <Button onClick={connect} size="lg" className="bg-green-600 hover:bg-green-700">
                      <Phone className="w-5 h-5 mr-2" />
                      {t.startInterview}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={toggleMute}
                        variant={isMuted ? 'destructive' : 'outline'}
                        size="lg"
                      >
                        {isMuted ? (
                          <MicOff className="w-5 h-5" />
                        ) : (
                          <Mic className="w-5 h-5" />
                        )}
                      </Button>
                      <Button
                        onClick={endInterview}
                        variant="destructive"
                        size="lg"
                      >
                        <PhoneOff className="w-5 h-5 mr-2" />
                        {t.endInterview}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {isSpeaking && (
                <div className="mt-4 flex items-center gap-2 text-primary">
                  <div className="flex gap-1">
                    <div className="w-1 h-4 bg-primary rounded animate-pulse" style={{ animationDelay: '0ms' }} />
                    <div className="w-1 h-4 bg-primary rounded animate-pulse" style={{ animationDelay: '150ms' }} />
                    <div className="w-1 h-4 bg-primary rounded animate-pulse" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm font-medium">{t.youAreSpeaking}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Conversation Transcript */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">{t.conversation}</h3>
              <div className="space-y-4 max-h-[500px] overflow-y-auto">
                {transcripts.length === 0 && connectionState !== 'connected' && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t.clickToStart}</p>
                  </div>
                )}

                {transcripts.map((item) => (
                  <div
                    key={item.id}
                    className={`flex gap-3 ${item.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {item.role === 'assistant' && (
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        {interview.journalist.photoUrl && (
                          <AvatarImage src={interview.journalist.photoUrl} />
                        )}
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                          {journalistInitials}
                        </AvatarFallback>
                      </Avatar>
                    )}

                    <div className={`max-w-[70%] ${
                      item.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    } rounded-lg p-3`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">
                          {item.role === 'user' ? interview.expertName : interview.journalist.name}
                        </span>
                        <span className="text-xs opacity-70">
                          {formatTime(item.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm">{item.text}</p>
                    </div>

                    {item.role === 'user' && (
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarFallback className="bg-purple-600 text-white text-xs">
                          <User className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                ))}

                {/* Current assistant transcript (streaming) */}
                {currentTranscript && (
                  <div className="flex gap-3 justify-start">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      {interview.journalist.photoUrl && (
                        <AvatarImage src={interview.journalist.photoUrl} />
                      )}
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {journalistInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="max-w-[70%] bg-muted rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">{interview.journalist.name}</span>
                        <Loader2 className="w-3 h-3 animate-spin" />
                      </div>
                      <p className="text-sm">{currentTranscript}</p>
                    </div>
                  </div>
                )}

                {isAssistantSpeaking && !currentTranscript && (
                  <div className="flex gap-3 justify-start">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      {interview.journalist.photoUrl && (
                        <AvatarImage src={interview.journalist.photoUrl} />
                      )}
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {journalistInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-muted rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium">{interview.journalist.name}</span>
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={transcriptEndRef} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side Panel - Hints & Questions */}
        <div className="space-y-4">
          {/* Current Question & Hints - Prominent when journalist is speaking */}
          <Card className={`transition-all duration-300 ${
            showHints && connectionState === 'connected'
              ? 'ring-2 ring-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30'
              : ''
          }`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className={`w-5 h-5 ${
                  showHints && connectionState === 'connected'
                    ? 'text-amber-500 animate-pulse'
                    : 'text-muted-foreground'
                }`} />
                <h3 className="font-semibold">{t.answerHints}</h3>
                {showHints && isAssistantSpeaking && (
                  <Badge variant="secondary" className="ml-auto bg-amber-100 text-amber-800 text-xs">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {t.listenAndPrepare}
                  </Badge>
                )}
              </div>

              {/* Current Question Display */}
              {interview.questions[currentQuestionIndex] && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    {t.questionOf.replace('{current}', String(currentQuestionIndex + 1)).replace('{total}', String(interview.questions.length))}
                  </div>
                  <div className={`p-3 rounded-lg border-2 transition-colors ${
                    showHints && connectionState === 'connected'
                      ? 'border-amber-400 bg-white dark:bg-gray-900'
                      : 'border-muted bg-muted'
                  }`}>
                    <p className="text-sm font-medium leading-relaxed">
                      {interview.questions[currentQuestionIndex].text}
                    </p>
                  </div>
                </div>
              )}

              {/* Example Answers - Most prominent */}
              {connectionState === 'connected' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <MessageSquareQuote className={`w-4 h-4 ${
                      showHints ? 'text-green-600' : 'text-muted-foreground'
                    }`} />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {t.trySaying}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {getExampleAnswers(interview.questions[currentQuestionIndex] || interview.questions[0]).map((example, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg transition-all ${
                          showHints
                            ? 'bg-green-50 dark:bg-green-950/30 border-2 border-green-300 dark:border-green-700'
                            : 'bg-muted/50 border border-muted'
                        }`}
                      >
                        <p className={`text-sm italic ${
                          showHints ? 'text-green-800 dark:text-green-200 font-medium' : 'text-muted-foreground'
                        }`}>
                          {example}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Talking Points - Secondary */}
              {connectionState === 'connected' && (
                <div className="space-y-2 mt-4 pt-4 border-t border-muted">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">
                      {t.tips}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {getHintsForQuestion(interview.questions[currentQuestionIndex] || interview.questions[0]).map((hint, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 py-1"
                      >
                        <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{hint}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {connectionState !== 'connected' && (
                <p className="text-sm text-muted-foreground italic">
                  {t.hintsWillAppear}
                </p>
              )}
            </CardContent>
          </Card>

          {/* All Questions List (collapsed) */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3 text-sm">{t.allQuestions}</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {interview.questions.map((question, index) => (
                  <div
                    key={question.id}
                    className={`p-2 rounded-lg transition-colors ${
                      index === currentQuestionIndex
                        ? 'bg-primary/10 border border-primary/30'
                        : questionsAskedRef.current.has(index)
                          ? 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800'
                          : 'bg-muted/50'
                    }`}
                  >
                    <div className="flex gap-2 items-start">
                      <span className={`font-semibold flex-shrink-0 text-xs ${
                        index === currentQuestionIndex ? 'text-primary' : 'text-muted-foreground'
                      }`}>
                        {index + 1}.
                      </span>
                      <span className="text-xs line-clamp-2">{question.text}</span>
                      {questionsAskedRef.current.has(index) && (
                        <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Interview Info */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">{t.aboutThisInterview}</h3>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div>
                  <p className="font-medium text-foreground mb-1">{t.aiVoiceInterview}</p>
                  <p>
                    {t.aiVoiceDescription.replace('{name}', interview.journalist.name)}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">{t.howItWorks}</p>
                  <ul className="space-y-1 list-disc list-inside">
                    {t.howItWorksList.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">{t.tipsTitle}</p>
                  <ul className="space-y-1 list-disc list-inside">
                    {t.tipsList.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Session Info */}
          {sessionId && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-2 text-sm">{t.sessionInfo}</h3>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p>{t.sessionId}: {sessionId.substring(0, 8)}...</p>
                  <p>{t.questions}: {interview.questions.length}</p>
                  <p>{t.transcripts}: {transcripts.length} {t.exchanges}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
